// <sortable-rows> — pointer-driven drag-sorting for a vertical list of rows.
// Light-DOM enhancement: rows are reordered in place, so form submission
// order (which the server derives priorities from) stays the source of
// truth. Pairs with <repeating-rows> by carrying its container attribute:
//
//   <sortable-rows class="block ..." data-repeating-rows-container>
//     <div data-sortable-rows-row data-repeating-rows-row>
//       <span data-sortable-rows-handle>⠿</span>
//       <span data-sortable-rows-position>#1</span>
//       ...fields...
//     </div>
//   </sortable-rows>
//
// Dragging: pointerdown on a handle lifts its row (shadow + accent ring) and
// the row tracks the pointer via transform; crossing a neighbour's vertical
// midpoint live-reorders the DOM, so the eventual drop position is always
// visible mid-drag. On release the row settles into its slot with a short
// transition. Position badges renumber after every reorder, and a
// MutationObserver keeps them honest across <repeating-rows> adds/removes —
// hidden rows (soft-removed persisted rows carrying _destroy=1) are skipped.
//
// Drag listeners live on the window, NOT on the handle, and pointer capture
// is deliberately not used: the mid-drag insertBefore momentarily disconnects
// the row, and disconnecting an element clears any pointer capture held
// inside it — which froze the drag after its first hop. Window listeners
// survive the reparenting; a pointerId check keeps multi-touch honest.

const LIFT = [
	"relative",
	"z-10",
	"scale-[1.01]",
	"shadow-2xl",
	"shadow-black/50",
	"ring-1",
	"ring-accent/60",
	"select-none",
];

class SortableRows extends HTMLElement {
	#abort?: AbortController;
	#dragAbort: AbortController | null = null;
	#observer?: MutationObserver;
	#dragging: HTMLElement | null = null;
	#handle: HTMLElement | null = null;
	#pointerId: number | null = null;
	#startY = 0;

	connectedCallback() {
		this.#abort?.abort(); // idempotent under Turbo restore
		this.#abort = new AbortController();
		this.addEventListener("pointerdown", (event) => this.#pointerDown(event), {
			signal: this.#abort.signal,
		});

		this.#observer?.disconnect();
		this.#observer = new MutationObserver(() => this.#renumber());
		this.#observer.observe(this, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["class", "hidden"],
		});
		this.#renumber();
	}

	disconnectedCallback() {
		this.#abort?.abort();
		this.#dragAbort?.abort();
		this.#observer?.disconnect();
	}

	// -- drag lifecycle --------------------------------------------------------

	#pointerDown(event: PointerEvent) {
		if (this.#dragging) this.#finish(); // never trap a stale drag

		const handle = (event.target as HTMLElement).closest<HTMLElement>(
			"[data-sortable-rows-handle]",
		);
		if (!handle || !this.#owns(handle)) return;
		const row = handle.closest<HTMLElement>("[data-sortable-rows-row]");
		if (!row || !this.#owns(row)) return;

		event.preventDefault(); // no text selection / scroll intent from the grip
		this.#dragging = row;
		this.#handle = handle;
		this.#pointerId = event.pointerId;
		this.#startY = event.clientY;

		row.style.transition = "none";
		row.classList.add(...LIFT);
		handle.classList.add("cursor-grabbing");

		this.#dragAbort = new AbortController();
		const { signal } = this.#dragAbort;
		window.addEventListener("pointermove", this.#pointerMove, { signal });
		window.addEventListener("pointerup", this.#pointerUp, { signal });
		window.addEventListener("pointercancel", this.#pointerUp, { signal });
	}

	#pointerMove = (event: PointerEvent) => {
		if (event.pointerId !== this.#pointerId) return;
		const row = this.#dragging;
		if (!row) return;

		event.preventDefault();
		row.style.transform = `translateY(${event.clientY - this.#startY}px)`;

		const target = this.#insertionTarget(event.clientY, row);
		if (target === this.#nextVisible(row)) return; // already in position

		// Reinsert, then compensate #startY by the layout jump so the row keeps
		// tracking the pointer without a visual snap (both rects include the
		// transform, so their difference is pure layout delta).
		const before = row.getBoundingClientRect().top;
		if (target) {
			target.parentElement?.insertBefore(row, target);
		} else {
			row.parentElement?.appendChild(row);
		}
		this.#startY += row.getBoundingClientRect().top - before;
		row.style.transform = `translateY(${event.clientY - this.#startY}px)`;
	};

	#pointerUp = (event: PointerEvent) => {
		if (event.pointerId !== this.#pointerId) return;
		this.#finish();
	};

	#finish() {
		this.#dragAbort?.abort();
		this.#dragAbort = null;
		this.#pointerId = null;

		const row = this.#dragging;
		const handle = this.#handle;
		this.#dragging = null;
		this.#handle = null;
		if (!row || !handle) return;

		handle.classList.remove("cursor-grabbing");

		// Settle into the slot, then drop the lift styling.
		row.style.transition = "transform 150ms ease";
		row.style.transform = "";
		window.setTimeout(() => {
			row.style.transition = "";
			row.classList.remove(...LIFT);
		}, 160);

		this.#renumber();
		this.dispatchEvent(
			new CustomEvent("sortable-rows:sorted", { bubbles: true }),
		);
	}

	// -- geometry ----------------------------------------------------------------

	// The first visible row (excluding the dragged one) whose vertical midpoint
	// sits below the pointer — the row to insert before. null appends.
	#insertionTarget(y: number, dragging: HTMLElement): HTMLElement | null {
		for (const candidate of this.#rows()) {
			if (candidate === dragging) continue;
			const rect = candidate.getBoundingClientRect();
			if (y < rect.top + rect.height / 2) return candidate;
		}
		return null;
	}

	#nextVisible(row: HTMLElement): HTMLElement | null {
		const rows = this.#rows();
		return rows[rows.indexOf(row) + 1] ?? null;
	}

	// -- housekeeping --------------------------------------------------------------

	#rows(): HTMLElement[] {
		return Array.from(
			this.querySelectorAll<HTMLElement>("[data-sortable-rows-row]"),
		).filter(
			(row) =>
				this.#owns(row) && !row.hidden && !row.classList.contains("hidden"),
		);
	}

	// Guard against nested <sortable-rows> capturing each other's rows/handles.
	#owns(node: HTMLElement): boolean {
		return node.closest("sortable-rows") === this;
	}

	#renumber() {
		this.#rows().forEach((row, index) => {
			const badge = row.querySelector<HTMLElement>(
				"[data-sortable-rows-position]",
			);
			if (!badge || !this.#owns(badge)) return;
			const label = `#${index + 1}`;
			// Only write on change — the observer watches this subtree, and an
			// unconditional write would re-trigger it forever.
			if (badge.textContent !== label) badge.textContent = label;
		});
	}
}

if (!customElements.get("sortable-rows")) {
	customElements.define("sortable-rows", SortableRows);
}
